import React, { useMemo } from 'react'
import { createTheme } from '@mui/material/styles'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { enUS, faIR } from '@mui/material/locale'
import { useLanguage } from '@/contexts/LanguageContext'

export const theme = createTheme({
  palette: {
    primary: {
      main: '#1A5F7A',
      light: '#4A8FA6',
      dark: '#0E4053',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2D9CDB',
      light: '#6BB0E2',
      dark: '#1C6D99',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#27AE60',
      light: '#52BE80',
      dark: '#1E8449',
    },
    warning: {
      main: '#F39C12',
      light: '#F7B731',
      dark: '#B9770E',
    },
    error: {
      main: '#E74C3C',
      light: '#EC7063',
      dark: '#CB4335',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C3E50',
      secondary: '#5D6D7E',
    },
  },
  typography: {
    fontFamily: '"Inter", "Noto Naskh Arabic", "Noto Sans", sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#1A5F7A',
            },
          },
        },
      },
    },
  },
})

export function getTheme(language: string) {
  const isRTL = language === 'dr' || language === 'ps'
  const locale = language === 'en' ? enUS : faIR
  return createTheme(
    {
      direction: isRTL ? 'rtl' : 'ltr',
      palette: theme.palette,
      typography: {
        ...theme.typography,
        fontFamily: isRTL
          ? '"Noto Naskh Arabic", "Inter", "Noto Sans", sans-serif'
          : '"Inter", "Noto Sans", "Noto Naskh Arabic", sans-serif',
      },
      shape: theme.shape,
      components: theme.components,
    },
    locale
  )
}

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { language } = useLanguage()
  const muiTheme = useMemo(() => getTheme(language), [language])

  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dir = muiTheme.direction
    }
  }, [muiTheme.direction])

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  )
}